/* 
    Internal component for the ChargerTableView page.
    It is used in the ChargerTableView component to display the parcels that are being compared.
*/

import React, { useState, useMemo } from 'react';
import { FaSort, FaSortUp, FaSortDown, FaSearch } from 'react-icons/fa';
import { useParcels } from '../../contexts/ParcelContext.jsx';

// Helper function to get numeric value from LADWP capacity range string for sorting
const getCapacityNumericValue = (capacityStr) => {
    if (!capacityStr || capacityStr === 'N/A' || capacityStr === 'NO CAPACITY') return -1;
    if (typeof capacityStr === 'number') return capacityStr;
    
    // Handle ">7500" case
    if (capacityStr.startsWith('>')) {
        return 7500;
    }
    
    // Handle range like "3000-3500" - use upper bound for sorting
    const match = capacityStr.match(/(\d+)-(\d+)/);
    if (match) {
        return parseInt(match[2]); // Return upper bound
    }
    
    // Try to parse as number
    const num = parseFloat(capacityStr);
    return isNaN(num) ? -1 : num;
};

const mapParcelToRow = (parcel) => {
    // Calculate capacity - handle both nearby_circuits array and nearby_circuits_ladwp
    let capacity = 'N/A';
    let capacitySortValue = -1;
    
    if (Array.isArray(parcel.nearby_circuits) && parcel.nearby_circuits.length > 0) {
        const capacities = parcel.nearby_circuits.map(circuit => circuit.capacity_kw).filter(cap => cap != null);
        if (capacities.length > 0) {
            capacity = Math.max(...capacities);
            capacitySortValue = capacity;
        }
    } else if (parcel.nearby_circuits_ladwp != null && parcel.nearby_circuits_ladwp !== 'N/A') {
        // Handle LADWP circuits - capacity is a string range
        if (Array.isArray(parcel.nearby_circuits_ladwp)) {
            // For multiple circuits, find the one with highest capacity using capacity_status or capacity_kw
            const sortedCircuits = [...parcel.nearby_circuits_ladwp].sort((a, b) => {
                const aVal = getCapacityNumericValue(a.capacity_kw);
                const bVal = getCapacityNumericValue(b.capacity_kw);
                return bVal - aVal;
            });
            if (sortedCircuits.length > 0) {
                capacity = sortedCircuits[0].capacity_kw ?? 'N/A';
                capacitySortValue = getCapacityNumericValue(capacity);
            }
        } else if (typeof parcel.nearby_circuits_ladwp === 'object') {
            // Single circuit object - capacity_kw is a string range
            capacity = parcel.nearby_circuits_ladwp.capacity_kw ?? 'N/A';
            capacitySortValue = getCapacityNumericValue(capacity);
        } else {
            // Simple value
            capacity = parcel.nearby_circuits_ladwp;
            capacitySortValue = getCapacityNumericValue(capacity);
        }
    }

    return {
        id: parcel.id ?? `${parcel.APN ?? ''}-${parcel.Zip5 ?? parcel.Zip ?? ''}-${parcel.Address ?? ''}`,
        address: parcel.Address ?? 'N/A',
        land_value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0, maximumFractionDigits: 0 }).format(parcel.Land_Value ?? 0),
        area: (parcel.Area ?? parcel.area ?? 0).toFixed(2).toString(),
        zone: parcel.zone_cla_1 ?? 'N/A',
        utility: parcel.utility_region_acronym ?? 'N/A',
        capacity: capacity,
        capacitySortValue: capacitySortValue, // Hidden field for sorting
    };
};

const columnHeaders = {
    'Address': 'address',
    'Land Value': 'land_value',
    'Area (sqft)': 'area',
    'Zone': 'zone',
    'Utility': 'utility',
    'Capacity (kW)': 'capacity',
}

const ParcelComparisonTable = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const { selectedParcels, clearSelectedParcels } = useParcels();

    // Get column headers (excluding id) and add recommendation scores
    const dataRows = useMemo(() => selectedParcels.map(mapParcelToRow), [selectedParcels]);
    
    // Find the parcel with the highest recommendation score for initial render
    const mostRecommendedParcelId = useMemo(() => {
        if (selectedParcels.length > 0) {
            return selectedParcels.reduce((a, b) => a.recommendation_score > b.recommendation_score ? a : b).id;
        }
    }, [selectedParcels]);

    // Handle sorting
    const handleSort = (columnDisplay) => {
        // Convert display name to data key
        const columnKey = columnHeaders[columnDisplay];
        let direction = 'asc';
        if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: columnKey, direction });
    };

    // Get sort icon for column
    const getSortIcon = (columnDisplay) => {
        const columnKey = columnHeaders[columnDisplay];
        if (sortConfig.key !== columnKey) {
            return <FaSort className="w-3 h-3 text-gray-400" />;
        }
        return sortConfig.direction === 'asc' 
            ? <FaSortUp className="w-3 h-3 text-blue-600" />
            : <FaSortDown className="w-3 h-3 text-blue-600" />;
    };

    // Filter and sort data
    const processedData = useMemo(() => {
        let filteredData = dataRows;

        // Apply search filter
        if (searchTerm) {
            filteredData = filteredData.filter(parcel =>
                Object.values(parcel).some(value =>
                    value.toString().toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }

        // Apply sorting
        if (sortConfig.key) {
            filteredData = [...filteredData].sort((a, b) => {
                // Special handling for capacity column - use capacitySortValue for numeric sorting
                if (sortConfig.key === 'capacity') {
                    const aNumeric = a.capacitySortValue ?? -1;
                    const bNumeric = b.capacitySortValue ?? -1;
                    return sortConfig.direction === 'asc' 
                        ? aNumeric - bNumeric 
                        : bNumeric - aNumeric;
                }

                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                // Handle numeric values
                const aNumeric = parseFloat(aValue);
                const bNumeric = parseFloat(bValue);
                
                if (!isNaN(aNumeric) && !isNaN(bNumeric)) {
                    return sortConfig.direction === 'asc' 
                        ? aNumeric - bNumeric 
                        : bNumeric - aNumeric;
                }

                // Handle string values
                const aString = aValue.toString().toLowerCase();
                const bString = bValue.toString().toLowerCase();
                
                if (aString < bString) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aString > bString) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filteredData;
    }, [searchTerm, sortConfig, dataRows]);

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Compare your land parcels</h1>
                {selectedParcels.length > 0 && (
                    <button
                        onClick={clearSelectedParcels}
                        className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50"
                    >
                        Clear Selected ({selectedParcels.length})
                    </button>
                )}
            </div>
            
            {/* Search Bar */}
            <div className="mb-6">
                <div className="relative max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaSearch className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search parcels..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                {searchTerm && (
                    <p className="mt-2 text-sm text-gray-600">
                         Found {processedData.length} result{processedData.length !== 1 ? 's' : ''} for "{searchTerm}"
                    </p>
                )}
            </div>

            {/* Table Container */}
            <div className="overflow-x-auto">
                <table className="min-w-full">
                    <thead>
                        <tr className="border-b border-gray-200">
                            {Object.keys(columnHeaders).map((column) => (
                                <th 
                                    key={column} 
                                    className="text-left p-4 font-semibold text-gray-700 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                                    onClick={() => handleSort(column)}
                                >
                                    <div className="flex items-center justify-between">
                                        <span>
                                            {column}
                                        </span>
                                        {getSortIcon(column)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {processedData.length === 0 ? (
                            <tr>
                                <td colSpan={Object.keys(columnHeaders).length} className="text-center py-8 text-gray-500">
                                    {searchTerm ? 'No parcels found matching your search.' : 'No selected parcels yet. Select parcels from the map view panel.'}
                                </td>
                            </tr>
                        ) : (
                            processedData.map((parcel, index) => {
                                const isRecommended = parcel.id === mostRecommendedParcelId;
                                return (
                                    <tr 
                                        key={parcel.id} 
                                        className={`hover:bg-gray-50 transition-colors ${
                                            index % 2 === 0 ? 'bg-white' : 'bg-gray-25'
                                        } ${isRecommended ? 'relative' : ''}`}
                                        style={{ marginBottom: '4px' }}
                                    >
                                        {Object.entries(columnHeaders).map(([displayName, dataKey], colIndex) => (
                                            <td 
                                                key={dataKey} 
                                                className={`text-left p-4 text-gray-900 border-b border-gray-100 ${
                                                    isRecommended && colIndex === 0 ? 'relative' : ''
                                                }`}
                                            >
                                                {/* Add recommendation tag to first cell of recommended row */}
                                            <div className="flex flex-row justify-between">
                                                {parcel[dataKey]}
                                                {isRecommended && colIndex === 0 && (
                                                    <div className="z-10">
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 shadow-sm">
                                                            ⭐ Best Score
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Results Summary */}
            {processedData.length > 0 && (
                <div className="mt-4 text-sm text-gray-600">
                    Showing {processedData.length} selected parcel{processedData.length !== 1 ? 's' : ''}
                    {sortConfig.key && (
                        <span className="ml-2">
                            • Sorted by {Object.keys(columnHeaders).find(key => columnHeaders[key] === sortConfig.key) || sortConfig.key} ({sortConfig.direction === 'asc' ? 'ascending' : 'descending'})
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default ParcelComparisonTable;